/**
 * Composable per Groups autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { GroupsAutocompleteOptions } from '@/lib/autocompletes';
import { getGroupsAutocomplete } from '@/lib/autocompletes/groups';
import { GroupAutocompleteResult } from '~/lib/models/group';

export function useGroupsAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const groups = ref<GroupAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchGroups(options: GroupsAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getGroupsAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					groups.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useGroupsAutocomplete.searchGroups:', err);
				error('Errore durante il caricamento dei gruppi! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					groups.value = [];
				}
			}
		}, 'searchGroups');
	}

	function clearGroups(): void {
		groups.value = [];
	}

	return {
		groups: readonly(groups),
		searchGroups,
		clearGroups,
	};
}
